import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../button/button.component';
import { ActivatedRoute } from '@angular/router';
import { MigrationsCiscoResource } from '../services/cisco/migrations-cisco.resource';
import { Observable, Subscription } from 'rxjs';
import { delayWhen, map } from 'rxjs/operators';
import { BreadcrumbsService } from '../../shared/breadcrumbs/breadcrumbs.service';
import { ToastService } from '../../shared/services/toast.service';
import { ToastTypes } from '../../shared/services/abstract/toast.service.abstract';
import { TranslateService } from '@ngx-translate/core';
import {
  AllExports,
  CiscoPbxExport,
  RingCentralExportDetails,
  RingCentralExportStatus,
} from '../../shared/models/generated/ziroModels';
import { ZiroModalService } from '../../shared/services/ziro-modal.service';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import {
  DatatableColumn,
  DatatableRow,
  DatatableRowActionButton,
  DatatableRowActionLink,
} from '../../shared/tables/datatable/datatable.models';
import { MigrationsDownloadFilesModalComponent } from '../../modals/migrations-download-files-modal/migrations-download-files-modal.component';
import { MigrationsAssessmentsContext } from '../services/migrations-assessments.context';
import { MigrationsRingCentralResource } from '../services/ring-central/migrations-ring-central.resource';
import { MigrationsAssessmentsPollingService } from '../services/migrations-assessments-polling.service';

interface MigrationsRow extends DatatableRow {
  name: string;
  pbxType: string;
}

@Component({
  selector: 'ziro-migrations-table',
  templateUrl: 'migrations-table.component.html',
  standalone: false,
})
export class MigrationsTableComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _translateService = inject(TranslateService);
  private _route = inject(ActivatedRoute);
  private _toastService = inject(ToastService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _migrationsAssessmentsPollingService = inject(MigrationsAssessmentsPollingService);
  private _migrationsCiscoResource = inject(MigrationsCiscoResource);
  private _migrationsRingCentralResource = inject(MigrationsRingCentralResource);
  private _ziroModalService = inject(ZiroModalService);

  columns: DatatableColumn<MigrationsRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.table.name',
      columnCharacterWidth: 40,
    },
    {
      name: 'pbxType',
      label: 'tkey;migrations.table.pbx_type',
      columnCharacterWidth: 15,
    },
  ];

  tableRows: MigrationsRow[] = [];
  isLoading = true;
  tableDataAutomation = 'migrations-table';
  tenantId: string;

  private _subscription = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;migrations.title' }]);
    this.tenantId = this._route.snapshot.params.tenantId;

    this._initBottomNav();
    const contextSub = this._migrationsAssessmentsContext.state$.subscribe((assessments) => {
      this._initTable(assessments);
      if (this._migrationsAssessmentsContext.isExportRefreshing(assessments)) {
        this._migrationsAssessmentsPollingService.startPollingIfStopped();
      } else {
        this._migrationsAssessmentsPollingService.stopPolling();
      }
      this.isLoading = false;
    });
    this._subscription.add(contextSub);

    const pollingSub = this._migrationsAssessmentsPollingService.state$.subscribe((assessments) => {
      this._migrationsAssessmentsContext.setState(assessments);
    });
    this._subscription.add(pollingSub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._migrationsAssessmentsPollingService.stopPolling();
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'migrations-table-new-cisco',
          label: 'tkey;migrations.table.new',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.ADD,
          dataAutomation: 'migrations-table-new-button',
          type: ButtonTypes.LINK,
          href: window.location.href + '/new',
        },
      ]),
    );
  }

  private _initTable(assessments: AllExports): void {
    const ciscoRows: MigrationsRow[] = assessments.ciscoPbxExports.map((assessment) => {
      return {
        name: assessment.name,
        pbxType: this._translateService.instant('tkey;migrations.table.pbx_type.cisco'),
        actions: [
          {
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.LINK,
            icon: ZiroIcons.NEXT,
            href: `${window.location.href}/cisco/${assessment.id}`,
            dataAutomation: 'migrations-table-next-button',
          },
          {
            style: ButtonStyles.OUTLINE_DANGER,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.DELETE_SMALL,
            dataAutomation: 'migrations-table-delete-button',
            onClick: () => {
              this._deleteCiscoAssessment(assessment);
            },
          },
          {
            style: ButtonStyles.OUTLINE_DEFAULT,
            type: ButtonTypes.BUTTON,
            label: this._translateService.instant(
              'tkey;migrations.cisco.reports_list.table.download_files.button.label',
            ),
            icon: ZiroIcons.DOWNLOAD,
            iconAppended: false,
            dataAutomation: 'migrations-table-download-files-button',
            onClick: () => {
              this._openDownloadFilesModal(assessment);
            },
          },
        ],
      };
    });
    const ringCentralRows: MigrationsRow[] = assessments.ringCentralExports.map((assessment) => {
      return {
        name: assessment.name,
        pbxType: this._translateService.instant('tkey;migrations.table.pbx_type.ring_central'),
        actions: this._getRingCentralActionButtons(assessment),
      };
    });
    this.tableRows = [...ciscoRows, ...ringCentralRows];
  }

  private _getRingCentralActionButtons(
    assessment: RingCentralExportDetails,
  ): (DatatableRowActionLink | DatatableRowActionButton<MigrationsRow>)[] {
    const deleteButton: DatatableRowActionButton<MigrationsRow> = {
      style: ButtonStyles.OUTLINE_DANGER,
      type: ButtonTypes.BUTTON,
      icon: ZiroIcons.DELETE_SMALL,
      dataAutomation: 'migrations-table-delete-button',
      onClick: () => {
        this._deleteRingCentralAssessment(assessment);
      },
    };
    switch (assessment.status) {
      case RingCentralExportStatus.COMPLETED: {
        const nextButton: DatatableRowActionLink = {
          style: ButtonStyles.OUTLINE_PRIMARY,
          type: ButtonTypes.LINK,
          icon: ZiroIcons.NEXT,
          href: `${window.location.href}/ring-central/${assessment.exportId}`,
          dataAutomation: 'migrations-table-next-button',
        };
        const downloadButton: DatatableRowActionButton<MigrationsRow> = {
          style: ButtonStyles.OUTLINE_DEFAULT,
          type: ButtonTypes.BUTTON,
          label: 'tkey;migrations.cisco.reports_list.table.download_files.button.label',
          icon: ZiroIcons.DOWNLOAD,
          iconAppended: false,
          dataAutomation: 'migrations-table-download-files-button',
          iconPending: ZiroIcons.SAVING,
          onClick: () => {
            downloadButton.isPending = true;
            this._migrationsRingCentralResource.downloadFiles(this.tenantId, assessment.exportId).subscribe(() => {
              downloadButton.isPending = false;
              this._toastService.push(
                ToastTypes.SUCCESS,
                ZiroIcons.DOWNLOAD,
                'tkey;migrations.cisco.reports_list.table.file_download.toast.title',
                'tkey;migrations.cisco.reports_list.table.file_download.toast.message',
                { fileType: 'RingCentral' },
              );
            });
          },
        };
        return [nextButton, deleteButton, downloadButton];
      }
      case RingCentralExportStatus.CREATING: {
        const fetchingButton: DatatableRowActionButton<MigrationsRow> = {
          style: ButtonStyles.OUTLINE_WARNING,
          type: ButtonTypes.BUTTON,
          icon: ZiroIcons.SAVING,
          onClick: () => {},
          dataAutomation: 'migrations-table-fetching-button',
          disabled: true,
          disabledTooltip: 'tkey;migrations.table.ring_central.fetching.tooltip',
          isFirstButton: true,
        };
        return [fetchingButton, deleteButton];
      }
      case RingCentralExportStatus.ERROR: {
        const errorButton: DatatableRowActionButton<MigrationsRow> = {
          style: ButtonStyles.OUTLINE_DANGER,
          type: ButtonTypes.BUTTON,
          icon: ZiroIcons.WARNING_TRIANGLE,
          onClick: () => {},
          dataAutomation: 'migrations-table-fetching-error-button',
          disabled: true,
          disabledTooltip: 'tkey;migrations.table.ring_central.fetching.error.tooltip',
          isFirstButton: true,
        };
        return [errorButton, deleteButton];
      }
    }
  }

  private _deleteCiscoAssessment(assessment: CiscoPbxExport): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant(
          'tkey;migrations.cisco.reports_list.table.delete_migration.modal.body',
          {
            name: assessment.name,
          },
        ),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDeleteCiscoAssessment(assessment),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onConfirmDeleteCiscoAssessment(assessment: CiscoPbxExport): Observable<void> {
    return this._migrationsCiscoResource.deleteAssessment(this.tenantId, assessment.id).pipe(
      delayWhen(() => this._migrationsAssessmentsContext.refresh()),
      map(() => {
        this._toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;migrations.cisco.reports_list.table.delete_migration.success.title',
          'tkey;migrations.cisco.reports_list.table.delete_migration.success.message',
          { name: assessment.name },
        );
      }),
    );
  }

  private _deleteRingCentralAssessment(assessment: RingCentralExportDetails): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant(
          'tkey;migrations.cisco.reports_list.table.delete_migration.modal.body',
          {
            name: assessment.name,
          },
        ),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDeleteRingCentralAssessment(assessment),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onConfirmDeleteRingCentralAssessment(assessment: RingCentralExportDetails): Observable<void> {
    return this._migrationsRingCentralResource.delete(this.tenantId, assessment.exportId).pipe(
      delayWhen(() => this._migrationsAssessmentsContext.refresh()),
      map(() => {
        this._toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;migrations.cisco.reports_list.table.delete_migration.success.title',
          'tkey;migrations.cisco.reports_list.table.delete_migration.success.message',
          { name: assessment.name },
        );
      }),
    );
  }

  private _openDownloadFilesModal(assessment: CiscoPbxExport): void {
    const baseDownloadUri = `/services/microsoft/migrations/tenants/${this.tenantId}/exports/cisco`;
    this._ziroModalService.openCustomModal(
      MigrationsDownloadFilesModalComponent,
      {
        reportTitle: assessment.name,
        buttons: {
          cucmTarExportDownloadUri: {
            title: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_tar.title',
            description: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_tar.description',
            label: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_tar.download.button.label',
            fileType: 'TAR',
            uri: assessment.isCucmTarPresent ? `${baseDownloadUri}/${assessment.id}/cucm-tars` : '',
          },
          cucmCdrDumpDownloadUri: {
            title: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_cdr_dump.title',
            description: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_cdr_dump.description',
            label: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cucm_cdr_dump.download.button.label',
            fileType: 'CDR',
            uri: assessment.isCucmCdrDumpPresent ? `${baseDownloadUri}/${assessment.id}/cdr-txts` : '',
          },
          // Hidden temporarily @#22452
          // cerExportDownloadUri: {
          //   title: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cer.title',
          //   description: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cer.description',
          //   label: 'tkey;migrations.cisco.reports_list.table.download_files.modal.cer.download.button.label',
          //   fileType: 'CER',
          //   uri: assessment.isCerExportPresent ? `${baseDownloadUri}/${assessment.id}/cer-zip` : '',
          // },
          cucExportDownloadUri: {
            title: 'tkey;migrations.cisco.reports_list.table.download_files.modal.unity_config.title',
            description: 'tkey;migrations.cisco.reports_list.table.download_files.modal.unity_config.description',
            label: 'tkey;migrations.cisco.reports_list.table.download_files.modal.unity_config.download.button.label',
            fileType: 'CUC',
            uri: assessment.isCucExportPresent ? `${baseDownloadUri}/${assessment.id}/cuc-zip` : '',
          },
        },
      },
      { size: 'xl' },
    );
  }
}
